


import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const mobileMenuRef = useRef(null);
  const hamburgerButtonRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleOutsideClick = (e) => {
    if (
      mobileMenuRef.current &&
      hamburgerButtonRef.current &&
      isMenuOpen &&
      !mobileMenuRef.current.contains(e.target) &&
      !hamburgerButtonRef.current.contains(e.target)
    ) {
      setIsMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  });

  const handleMenuToggle = () => setIsMenuOpen(!isMenuOpen);
  const handleMenuItemClick = () => setIsMenuOpen(false);

  const isActiveRoute = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname === path || location.pathname.startsWith(path + "/");

  const navLinks = [
    { name: "Home", path: "/" },
    // { name: "Services", path: "/services" },
    { name: "Industry", path: "/industries" },
    { name: "Process", path: "/process" },
    { name: "About", path: "/about" },
  ];

  return (
    <>
      <nav
        className={`fixed w-full top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/80 backdrop-blur-xl py-3 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">

          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 overflow-hidden relative">
              <img
                src="/images/Logo12png.png"
                alt="ViconAI Logo"
                className="w-14 h-14 object-cover overflow-hidden relative z-10"
              />
            </div>
            <span className="text-2xl font-black text-[#17091a] tracking-tight">
              Vicon<span className="text-[#952877]">AI</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-10">

            {/* Home */}
            <Link
              to="/"
              className={`text-sm font-bold tracking-widest uppercase transition-all duration-300 relative py-2 group ${
                isActiveRoute("/") ? "text-[#952877]" : "text-[#17091a]/70 hover:text-[#17091a]"
              }`}
            >
              Home
              <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#952877] transition-all duration-300 ${
                isActiveRoute("/") ? "opacity-100 scale-100" : "opacity-0 scale-0 group-hover:opacity-50 group-hover:scale-100"
              }`}></span>
            </Link>

            {/* Products — plain link, no dropdown */}
            <Link
              to="/products"
              className={`text-sm font-bold tracking-widest uppercase transition-all duration-300 relative py-2 group ${
                isActiveRoute("/products") ? "text-[#952877]" : "text-[#17091a]/70 hover:text-[#17091a]"
              }`}
            >
              Products
              <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#952877] transition-all duration-300 ${
                isActiveRoute("/products") ? "opacity-100 scale-100" : "opacity-0 scale-0 group-hover:opacity-50 group-hover:scale-100"
              }`}></span>
            </Link>

            {/* Remaining Nav Links */}
            {navLinks.filter((l) => l.path !== "/").map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-bold tracking-widest uppercase transition-all duration-300 relative py-2 group ${
                  isActiveRoute(link.path) ? "text-[#952877]" : "text-[#17091a]/70 hover:text-[#17091a]"
                }`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#952877] transition-all duration-300 ${
                  isActiveRoute(link.path) ? "opacity-100 scale-100" : "opacity-0 scale-0 group-hover:opacity-50 group-hover:scale-100"
                }`}></span>
              </Link>
            ))}
          </div>

          {/* CTA + Hamburger */}
          <div className="flex items-center space-x-5">
            <Link
              to="/contact"
              className="hidden sm:block relative overflow-hidden px-8 py-3 rounded-2xl bg-[#17091a] text-white font-bold text-sm tracking-widest uppercase shadow-xl hover:shadow-[#952877]/20 hover:scale-105 transition-all duration-300 active:scale-95 group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#952877] to-[#592aa7] opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <span className="relative z-10">Let's Talk</span>
            </Link>

            <button
              ref={hamburgerButtonRef}
              onClick={handleMenuToggle}
              className="md:hidden p-2 text-[#17091a] relative w-10 h-10 flex flex-col justify-center items-center rounded-xl bg-gray-50"
            >
              <div className={`w-5 h-0.5 bg-current transform transition-all duration-300 ${
                isMenuOpen ? "rotate-45 translate-y-0.5" : "-translate-y-1"
              }`}></div>
              <div className={`w-5 h-0.5 bg-current transform transition-all duration-300 mt-1 ${
                isMenuOpen ? "-rotate-45 -translate-y-1" : "translate-y-0"
              }`}></div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          ref={mobileMenuRef}
          className={`md:hidden absolute top-full left-0 w-full bg-white/95 backdrop-blur-2xl border-t border-gray-100 transition-all duration-500 ease-in-out ${
            isMenuOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-10 pointer-events-none"
          }`}
        >
          <div className="p-6 space-y-3">

            {/* Home */}
            <Link
              to="/"
              className={`block px-5 py-4 rounded-2xl font-bold tracking-widest uppercase text-center transition-all ${
                isActiveRoute("/")
                  ? "bg-[#17091a] text-white shadow-lg shadow-purple-200"
                  : "text-[#17091a] hover:bg-gray-50"
              }`}
              onClick={handleMenuItemClick}
            >
              Home
            </Link>

            {/* Products — plain link, no accordion */}
            <Link
              to="/products"
              className={`block px-5 py-4 rounded-2xl font-bold tracking-widest uppercase text-center transition-all ${
                isActiveRoute("/products")
                  ? "bg-[#17091a] text-white shadow-lg shadow-purple-200"
                  : "text-[#17091a] hover:bg-gray-50"
              }`}
              onClick={handleMenuItemClick}
            >
              Products
            </Link>

            {/* Remaining Links + Contact */}
            {navLinks
              .filter((l) => l.path !== "/")
              .concat([{ name: "Contact", path: "/contact" }])
              .map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-5 py-4 rounded-2xl font-bold tracking-widest uppercase text-center transition-all ${
                    isActiveRoute(link.path)
                      ? "bg-[#17091a] text-white shadow-lg shadow-purple-200"
                      : "text-[#17091a] hover:bg-gray-50"
                  }`}
                  onClick={handleMenuItemClick}
                >
                  {link.name}
                </Link>
              ))}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navigation;